import { calculateGoodfillTransactionCommissionForAmount } from "@goodfill/domain-math";
import { asBondPriceNominal, asYield, asAmount, OrderDirection, Currency } from "@goodfill/domain-properties";
import {
  BalanceAccountAmountComponent,
  FaceAmountComponent,
  SettlementValueComponent,
  TradeValueComponent,
} from "@goodfill/domain-properties-components";
import { formatCurrencyAmount } from "@goodfill/format-number";
import { mapMapValues } from "@goodfill/map";
import Decimal from "decimal.js";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { amplifyErrorsToString } from "../../../amplifyErrorsToString";
import { FormValidation, isFormValid } from "../../../form/formValidation";
import { FormValidationComponent } from "../../../form/FormValidationComponent";
import { TableHeader, TableRow, Table, Button } from "../../../ui-components";
import { PresentedPortfolioPosition } from "../../account/portfolio/positions/PresentedPortfolioPosition";
import { UserAuthDataBalanceAccount } from "../../backendApi/amplifyGraphQl/CustomGetUserData/UserData";
import { useRefreshUserDataHook } from "../../useRefreshUserDataHook";
import { createOrderBackendCall } from "../orderForm/createOrder/createOrderBackendCall";
import { OrderFormState } from "../orderForm/state/OrderFormState";
import { CreateOrderStateValidated, validateCreateOrderState } from "../orderForm/state/validateCreateOrderState";
import { BatchOrderLineLifecycleStatus, BatchOrderLineState, updateByRows } from "./BatchOrderLineState";
import { DoNotCloseLoadingNote } from "./DoNotCloseLoadingNote";
import { OrderExpirationTimeTableHeader } from "./OrderExpirationTimeTableHeader";

const header: TableHeader<
  | keyof PresentedPortfolioPosition
  | "_status"
  | "_delete"
  | "transactionCostAmount"
  | "transactionRateUpfront"
  | "loadedSellPrice"
  | "loadedSellYield"
  | "yieldAtExpiration"
  | "expirationDateTime"
  | "timeInForce"
>[] = [
  { key: "_status", value: "", sortable: false },
  { key: "_delete", value: "", sortable: false },

  // Bond info
  { key: "ticker", value: "Ticker" },
  { key: "issuerName", value: "Issuer Name" },
  { key: "couponPayment", value: "Coupon Payment" },
  { key: "maturityDate", value: "Maturity Date" },

  // Inputs
  {
    key: "currentLoadedBidPrice",
    value: (
      <>
        Price
        <br />
        Post-Fee
      </>
    ),
    sortable: false,
  },
  { key: "amount", value: "Amount", sortable: false },
  {
    key: "loadedSellYield",
    value: (
      <>
        Yield
        <br />
        Post-Fee
      </>
    ),
    sortable: false,
  },
  {
    key: "expirationDateTime",
    value: <OrderExpirationTimeTableHeader />,
    sortable: false,
  },

  // Sell order info
  {
    key: "yieldAtExpiration",
    value: (
      <>
        Yield at expiration
        <br />
        Post-fee
      </>
    ),
  },
  {
    key: "transactionCostAmount",
    value: (
      <>
        Transaction
        <br />
        cost amount
      </>
    ),
  },
  {
    key: "transactionRateUpfront",
    value: (
      <>
        Transaction
        <br />
        rate upfront
      </>
    ),
  },
];

export const BatchDialogTableContainer = <TKey, TRow>({
  direction,
  rows,
  moneyBalanceAccount,
  rowToId,
  rowToState,
  tableRowRenderer,
}: {
  direction: OrderDirection;
  rows: TRow[];
  moneyBalanceAccount: UserAuthDataBalanceAccount;
  rowToId: (row: TRow) => TKey;
  rowToState: (row: TRow) => OrderFormState;
  tableRowRenderer: (data: {
    changeStatePrice: (value: number | null) => void;
    changeYield: (value: number | null) => void;
    changeAmount: (value: number | null) => void;
    setExpirationDate: (value: DateTime) => void;
    validation: FormValidation<CreateOrderStateValidated>;
    state: BatchOrderLineState<TRow>;
    disabled: boolean;
  }) => TableRow;
}) => {
  const refreshUser = useRefreshUserDataHook();

  const [rowStates, setRowStates] = useState<Map<TKey, BatchOrderLineState<TRow>>>(new Map());

  useEffect(() => {
    setRowStates((currentStates) => updateByRows(currentStates, rows, rowToId, rowToState));
  }, [rows, rowToId, rowToState]);

  const currentDate = DateTime.local();

  const validations = mapMapValues(rowStates, (orderState) => {
    return validateCreateOrderState(orderState.orderState, {
      currentDate,
      availableBalance: moneyBalanceAccount.disponibleBalance,
    });
  });

  const updateRowState = (key: TKey, update: (value: BatchOrderLineState<TRow>) => BatchOrderLineState<TRow>) => {
    setRowStates((currentStates) => {
      const newMap = new Map(currentStates);
      const element = newMap.get(key);
      if (element !== undefined) {
        newMap.set(key, update(element));
      }

      return newMap;
    });
  };

  const updateRowLifeCycle = (key: TKey, lifecycleStatus: BatchOrderLineLifecycleStatus) => {
    updateRowState(key, (element) => ({ ...element, lifecycleStatus }));
  };

  const handleSellAll = async () => {
    for (const [rowKey, rowState] of rowStates.entries()) {
      updateRowLifeCycle(rowKey, "loading");
      const state = rowState.orderState;
      const rateCommission = calculateGoodfillTransactionCommissionForAmount(state.s.faceAmount);

      try {
        await createOrderBackendCall(
          state.i.pricedBond,
          state.getBasePrice(),
          moneyBalanceAccount.id,
          state.s.faceAmount.amount,
          state.s.isAllOrNothing,
          state.s.customLoadedYield,
          rateCommission,
          state.isImmediate(),
          state.s.expirationDate,
          state.i.orderDirection,
          state.i.closingTradeTicketId,
        );
        updateRowLifeCycle(rowKey, "done");
      } catch (e) {
        console.error(e);
        const eMessage = amplifyErrorsToString(e);
        toast.error(eMessage);
        updateRowLifeCycle(rowKey, "failed");
      }
    }

    await refreshUser();
  };

  const rowStatesEntriesAsArray = Array.from(rowStates.entries());
  const validationsAsArray = Array.from(validations.values());

  const isNotEmpty = rowStatesEntriesAsArray.length > 0;

  const isAllPrepare = isNotEmpty && rowStatesEntriesAsArray.every(([_, a]) => a.lifecycleStatus === "prepare");
  const isAnyLoading =
    isNotEmpty && rowStatesEntriesAsArray.find(([_, a]) => a.lifecycleStatus === "loading") !== undefined;
  const allDisabled = !isAllPrepare;

  const isAllValid = validationsAsArray.every(isFormValid);

  const totalSettlementValue = asAmount(
    rowStatesEntriesAsArray.reduce(
      (acc, [_, state]) => acc.plus(state.orderState.s.faceAmount.amount.mul(state.orderState.s.customLoadedPrice)),
      new Decimal(0),
    ),
  );

  const globalValidation =
    direction === OrderDirection.Buy && totalSettlementValue.greaterThan(moneyBalanceAccount.disponibleBalance)
      ? [
          <>
            Total settlement value <SettlementValueComponent value={totalSettlementValue} /> is greater then your
            available balance{" "}
            <BalanceAccountAmountComponent
              value={moneyBalanceAccount.disponibleBalance}
              currency={moneyBalanceAccount.currency ?? Currency.USD}
            />
          </>,
        ]
      : [];

  const disableSubmit = allDisabled || !isAllValid || globalValidation.length > 0;

  const tableRows: TableRow[] = rowStatesEntriesAsArray.map(([rowKey, rowState]) => {
    const orderState = rowState.orderState;
    const validation = validations.get(rowKey)!; // Todo: solve this

    const isLineDisabled = allDisabled || rowState.lifecycleStatus !== "prepare";

    const changeStatePrice = (value: number | null) => {
      if (!isLineDisabled && value !== null) {
        updateRowState(rowKey, (element) => ({
          ...element,
          orderState: orderState.withCustomLoadedPrice(asBondPriceNominal(new Decimal(value).div(100)), currentDate),
        }));
      }
    };

    const changeYield = (value: number | null) => {
      if (!isLineDisabled && value !== null) {
        updateRowState(rowKey, (element) => ({
          ...element,
          orderState: orderState.withCustomLoadedYield(asYield(new Decimal(value).div(100)), currentDate),
        }));
      }
    };

    const changeAmount = (value: number | null) => {
      if (!isLineDisabled && value !== null) {
        updateRowState(rowKey, (element) => ({
          ...element,
          orderState: orderState.withFaceAmount(asAmount(new Decimal(value))),
        }));
      }
    };

    const setExpirationDate = (value: DateTime) => {
      if (!isLineDisabled) {
        updateRowState(rowKey, (element) => ({
          ...element,
          orderState: orderState.withExpirationDate(value),
        }));
      }
    };

    return tableRowRenderer({
      changeStatePrice,
      changeYield,
      changeAmount,
      setExpirationDate,
      disabled: isLineDisabled,
      validation,
      state: rowState,
    });
  });

  const totalFaceMountToSell = asAmount(
    rowStatesEntriesAsArray.reduce(
      (acc, [_, state]) => acc.plus(state.orderState.s.faceAmount.amount),
      new Decimal(0),
    ),
  );

  return (
    <div className="flex flex-col gap-4">
      {isAnyLoading ? <DoNotCloseLoadingNote /> : null}
      <Table header={header} rows={tableRows} />
      <FormValidationComponent validation={globalValidation} />
      <Button fill red onClick={handleSellAll} disabled={disableSubmit} loading={isAnyLoading}>
        {direction === OrderDirection.Buy ? "Buy" : "Sell"} position with total amount{" "}
        <FaceAmountComponent value={totalFaceMountToSell} /> for{" "}
        <SettlementValueComponent value={totalSettlementValue} />
      </Button>
    </div>
  );
};
